import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Ingredient } from '../../services/mockData';

interface IngredientDatabaseProps {
  onSelectIngredient: (ingredient: Ingredient) => void;
  selectedIngredients?: Ingredient[];
}

const INGREDIENT_CATEGORIES = [
  { id: 'all', label: 'Todos' },
  { id: 'vitamins', label: 'Vitaminas' },
  { id: 'minerals', label: 'Minerais' },
  { id: 'aminoacids', label: 'Aminoácidos' },
  { id: 'probiotics', label: 'Probióticos' },
  { id: 'other', label: 'Outros' },
];

const BASE_INGREDIENTS: Array<Ingredient & { category: string; commonDosage: string }> = [
  // Vitaminas
  { name: 'Vitamina D3', quantity: 0, unit: 'UI', category: 'vitamins', commonDosage: '2000-5000 UI/dia', purpose: 'Suporte ósseo e imunológico' },
  { name: 'Vitamina B12', quantity: 0, unit: 'mcg', category: 'vitamins', commonDosage: '100-1000 mcg/dia', purpose: 'Energia e função neurológica' },
  { name: 'Ácido Fólico', quantity: 0, unit: 'mcg', category: 'vitamins', commonDosage: '400-800 mcg/dia', purpose: 'Síntese de DNA e divisão celular' },
  { name: 'Vitamina C', quantity: 0, unit: 'mg', category: 'vitamins', commonDosage: '500-2000 mg/dia', purpose: 'Antioxidante e imunidade' },
  { name: 'Vitamina E', quantity: 0, unit: 'UI', category: 'vitamins', commonDosage: '200-400 UI/dia', purpose: 'Antioxidante' },
  { name: 'Vitamina A', quantity: 0, unit: 'UI', category: 'vitamins', commonDosage: '5000-10000 UI/dia', purpose: 'Visão e imunidade' },
  
  // Minerais
  { name: 'Ferro', quantity: 0, unit: 'mg', category: 'minerals', commonDosage: '15-30 mg/dia', purpose: 'Transporte de oxigênio' },
  { name: 'Magnésio', quantity: 0, unit: 'mg', category: 'minerals', commonDosage: '200-400 mg/dia', purpose: 'Função muscular e nervosa' },
  { name: 'Zinco', quantity: 0, unit: 'mg', category: 'minerals', commonDosage: '8-15 mg/dia', purpose: 'Imunidade e cicatrização' },
  { name: 'Cálcio', quantity: 0, unit: 'mg', category: 'minerals', commonDosage: '500-1000 mg/dia', purpose: 'Saúde óssea' },
  { name: 'Selênio', quantity: 0, unit: 'mcg', category: 'minerals', commonDosage: '55-200 mcg/dia', purpose: 'Antioxidante' },
  { name: 'Cromo', quantity: 0, unit: 'mcg', category: 'minerals', commonDosage: '50-200 mcg/dia', purpose: 'Metabolismo de glicose' },
  
  // Aminoácidos
  { name: 'BCAA', quantity: 0, unit: 'g', category: 'aminoacids', commonDosage: '5-10 g/dia', purpose: 'Recuperação muscular' },
  { name: 'L-Glutamina', quantity: 0, unit: 'g', category: 'aminoacids', commonDosage: '5-15 g/dia', purpose: 'Recuperação e imunidade' },
  { name: 'Creatina', quantity: 0, unit: 'g', category: 'aminoacids', commonDosage: '3-5 g/dia', purpose: 'Força e potência muscular' },
  { name: 'L-Carnitina', quantity: 0, unit: 'g', category: 'aminoacids', commonDosage: '1-3 g/dia', purpose: 'Metabolismo de gordura' },
  { name: 'Taurina', quantity: 0, unit: 'g', category: 'aminoacids', commonDosage: '1-3 g/dia', purpose: 'Função cardíaca e ocular' },
  
  // Probióticos
  { name: 'Lactobacillus acidophilus', quantity: 0, unit: 'UFC', category: 'probiotics', commonDosage: '1-10 bilhões UFC/dia', purpose: 'Saúde intestinal' },
  { name: 'Bifidobacterium', quantity: 0, unit: 'UFC', category: 'probiotics', commonDosage: '1-10 bilhões UFC/dia', purpose: 'Saúde intestinal' },
  { name: 'Lactobacillus rhamnosus', quantity: 0, unit: 'UFC', category: 'probiotics', commonDosage: '1-10 bilhões UFC/dia', purpose: 'Saúde intestinal' },
  
  // Outros
  { name: 'Ômega 3', quantity: 0, unit: 'mg', category: 'other', commonDosage: '1000-3000 mg/dia', purpose: 'Anti-inflamatório e saúde cardiovascular' },
  { name: 'Coenzima Q10', quantity: 0, unit: 'mg', category: 'other', commonDosage: '100-300 mg/dia', purpose: 'Energia celular' },
  { name: 'Colágeno', quantity: 0, unit: 'g', category: 'other', commonDosage: '5-10 g/dia', purpose: 'Saúde articular e da pele' },
  { name: 'Melatonina', quantity: 0, unit: 'mg', category: 'other', commonDosage: '1-5 mg/dia', purpose: 'Regulação do sono' },
  { name: 'Proteína Whey', quantity: 0, unit: 'g', category: 'other', commonDosage: '20-40 g/dia', purpose: 'Suporte proteico' },
];

export const IngredientDatabase: React.FC<IngredientDatabaseProps> = ({
  onSelectIngredient,
  selectedIngredients = [],
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customIngredient, setCustomIngredient] = useState({
    name: '',
    quantity: '',
    unit: 'mg',
    purpose: '',
  });

  const filteredIngredients = BASE_INGREDIENTS.filter(ing => {
    const matchesSearch = searchTerm === '' || 
      ing.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || ing.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleAddCustom = () => {
    if (!customIngredient.name || !customIngredient.quantity) {
      alert('Preencha nome e quantidade do ingrediente');
      return;
    }

    const ingredient: Ingredient = {
      name: customIngredient.name,
      quantity: parseFloat(customIngredient.quantity),
      unit: customIngredient.unit,
      purpose: customIngredient.purpose || undefined,
    };

    onSelectIngredient(ingredient);
    setCustomIngredient({ name: '', quantity: '', unit: 'mg', purpose: '' });
    setShowAddCustom(false);
  };

  const isSelected = (ingredient: Ingredient) => {
    return selectedIngredients.some(
      sel => sel.name === ingredient.name && sel.unit === ingredient.unit
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {INGREDIENT_CATEGORIES.map(cat => (
          <button
            key={cat.id}
            type="button"
            onClick={() => setSelectedCategory(cat.id)}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              background: selectedCategory === cat.id ? '#0ea5e9' : '#ffffff',
              color: selectedCategory === cat.id ? '#ffffff' : '#0f172a',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <input
        type="text"
        className="ns-input"
        placeholder="Buscar ingrediente..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ width: '100%', marginBottom: '12px' }}
      />

      <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '12px' }}>
        {filteredIngredients.map((ing, index) => {
          const selected = isSelected(ing);
          return (
            <div
              key={index}
              style={{
                padding: '12px',
                background: selected ? '#eff6ff' : '#f8fafc',
                border: `1px solid ${selected ? '#0ea5e9' : '#e2e8f0'}`,
                borderRadius: '8px',
                marginBottom: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>
                  {ing.name}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--page-text-muted)', marginBottom: '2px' }}>
                  Dose comum: {ing.commonDosage}
                </div>
                {ing.purpose && (
                  <div style={{ fontSize: '11px', color: 'var(--page-text-muted)' }}>
                    {ing.purpose}
                  </div>
                )}
              </div>
              <Button
                variant={selected ? 'primary' : 'secondary'}
                onClick={() => {
                  const ingredient: Ingredient = {
                    name: ing.name,
                    quantity: 0,
                    unit: ing.unit,
                    purpose: ing.purpose,
                  };
                  onSelectIngredient(ingredient);
                }}
                style={{ fontSize: '11px', padding: '4px 8px', marginLeft: '8px' }}
              >
                {selected ? '✓ Selecionado' : 'Adicionar'}
              </Button>
            </div>
          );
        })}
      </div>

      <Button
        variant="secondary"
        onClick={() => setShowAddCustom(!showAddCustom)}
        style={{ width: '100%', marginBottom: '12px' }}
      >
        {showAddCustom ? '✕ Cancelar' : '➕ Adicionar Ingrediente Customizado'}
      </Button>

      {showAddCustom && (
        <div style={{
          padding: '16px',
          background: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
        }}>
          <label className="ns-field">
            <span>Nome do Ingrediente *</span>
            <input
              type="text"
              className="ns-input"
              value={customIngredient.name}
              onChange={(e) => setCustomIngredient({ ...customIngredient, name: e.target.value })}
              placeholder="Ex: Vitamina K2"
            />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
            <label className="ns-field">
              <span>Quantidade *</span>
              <input
                type="number"
                step="0.1"
                className="ns-input"
                value={customIngredient.quantity}
                onChange={(e) => setCustomIngredient({ ...customIngredient, quantity: e.target.value })}
                placeholder="0"
              />
            </label>
            <label className="ns-field">
              <span>Unidade *</span>
              <select
                className="ns-input"
                value={customIngredient.unit}
                onChange={(e) => setCustomIngredient({ ...customIngredient, unit: e.target.value })}
              >
                <option value="mg">mg</option>
                <option value="g">g</option>
                <option value="mcg">mcg</option>
                <option value="UI">UI</option>
                <option value="UFC">UFC</option>
                <option value="ml">ml</option>
              </select>
            </label>
          </div>
          <label className="ns-field" style={{ marginTop: '12px' }}>
            <span>Finalidade (Opcional)</span>
            <input
              type="text"
              className="ns-input"
              value={customIngredient.purpose}
              onChange={(e) => setCustomIngredient({ ...customIngredient, purpose: e.target.value })}
              placeholder="Ex: Para ganho de massa muscular"
            />
          </label>
          <Button
            onClick={handleAddCustom}
            style={{ marginTop: '12px', width: '100%' }}
          >
            Adicionar Ingrediente
          </Button>
        </div>
      )}
    </div>
  );
};

